export const warehousePageData = {
  stats: [
    { value: "50%", label: "Less Labour Time Per Order" },
    { value: "99.9%", label: "Picking Accuracy" },
    { value: "40%", label: "Space Utilisation Improvement" },
    { value: "Real-Time", label: "Stock Visibility" },
  ],
  statsAccent: "from-slate-700 to-zinc-800",

  challengeTitle: "Your Warehouse Is Costing You More Than It Should",
  challengeSubtitle:
    "Manual warehouse management creates hidden costs that quietly destroy your operating margins every single day.",
  challenges: [
    {
      icon: "🔍",
      title: "Staff Searching, Not Picking",
      description:
        "Without optimised putaway and bin locations, warehouse staff spend more time locating products than picking and packing.",
    },
    {
      icon: "❌",
      title: "High Mis-Pick Rate",
      description:
        "Manual pick lists and paper-based processes result in mis-picks that trigger returns, refunds, and customer complaints.",
    },
    {
      icon: "📊",
      title: "Inaccurate Stock Records",
      description:
        "Stock counts, receipts, and adjustments handled manually create discrepancies between system records and actual inventory.",
    },
    {
      icon: "📦",
      title: "Overselling & Order Failures",
      description:
        "Without real-time stock updates, products that are sold out remain visible online — causing orders that can't be fulfilled.",
    },
    {
      icon: "🏢",
      title: "Wasted Warehouse Space",
      description:
        "Without slotting optimisation, slow-moving products occupy prime picking locations while fast movers are in distant corners.",
    },
    {
      icon: "👁️",
      title: "No Multi-Warehouse Visibility",
      description:
        "Managing inventory across multiple sites from disconnected systems creates blind spots and costly inter-site miscommunication.",
    },
  ],
  theme: {
    challengeBg: "bg-slate-900",
    challengeTitle: "text-white",
    challengeSubtitle: "text-slate-400",
    challengeCard: "bg-slate-800/60",
    challengeBorder: "border-slate-700",
    badgeBg: "bg-slate-100 text-slate-700 border-slate-200",
    solutionIconBg: "bg-slate-100",
    solutionIconColor: "text-slate-700",
    solutionAccent: "from-slate-700 to-zinc-700",
  },

  solutionTitle: "Intelligent Warehouse Operations Powered by AI",
  solutionSubtitle:
    "Turn your warehouse into a precision operation — with real-time inventory tracking, AI-optimised picking, and automated availability management.",
  solutions: [
    {
      icon: "📡",
      title: "Real-Time Inventory Tracking",
      description:
        "Every product movement is tracked and timestamped automatically — from goods-in to dispatch, with zero manual data entry.",
    },
    {
      icon: "🤖",
      title: "AI-Optimised Pick Paths",
      description:
        "AI generates pick routes that minimise walking distance — cutting pick time per order by 50% and eliminating mis-picks.",
    },
    {
      icon: "📱",
      title: "Barcode Scanning Validation",
      description:
        "Real-time barcode scanning validates every pick, confirms quantities, and prevents wrong-item errors at source.",
    },
    {
      icon: "🔄",
      title: "Automated Availability Management",
      description:
        "Products auto-publish when in stock and auto-unpublish when depleted across all sales channels simultaneously.",
    },
    {
      icon: "🏢",
      title: "Intelligent Slotting",
      description:
        "AI places fast-moving items in the most accessible bin locations — maximising warehouse throughput automatically.",
    },
    {
      icon: "📦",
      title: "Multi-Warehouse Management",
      description:
        "Unified control across unlimited warehouse sites with real-time stock visibility and inter-site transfer management.",
    },
    {
      icon: "🔔",
      title: "Automated Replenishment",
      description:
        "Warehouse replenishment zones trigger purchase orders automatically when bin quantities fall below threshold.",
    },
    {
      icon: "📊",
      title: "Warehouse Performance Analytics",
      description:
        "Labour cost per order, pick accuracy, throughput rates, and space utilisation — all tracked in one dashboard.",
    },
    {
      icon: "🔌",
      title: "ERP & E-Commerce Integration",
      description:
        "Bidirectional sync with ERP and e-commerce platforms ensures inventory data is always accurate across every system.",
    },
  ],

  howItWorksTitle: "Smarter Warehouse Operations From Day One",
  howItWorksSteps: [
    {
      icon: "🗂️",
      title: "Map Your Warehouse",
      description: "Configure bin locations, zones, and putaway rules in the EkkBaz AI platform — takes under 2 hours.",
    },
    {
      icon: "📡",
      title: "Go Live with Tracking",
      description: "Start scanning items in and out. Real-time stock records are built automatically from the first movement.",
    },
    {
      icon: "🤖",
      title: "AI Optimises Operations",
      description: "Pick paths, slotting, and replenishment triggers are all optimised by AI continuously.",
    },
    {
      icon: "📈",
      title: "Costs Drop, Accuracy Rises",
      description: "Monitor labour cost savings, accuracy improvements, and throughput gains in your live dashboard.",
    },
  ],

  outcomeTitle: "Warehouse Efficiency That Moves the Bottom Line",
  outcomeSubtitle: "Real outcomes from businesses using EkkBaz AI Warehouse Management.",
  outcomes: [
    {
      value: "50%",
      metric: "Less Labour Time Per Order",
      description:
        "AI-optimised pick paths and bin slotting reduce the time from pick request to packed order by 50% — cutting labour costs significantly.",
    },
    {
      value: "99.9%",
      metric: "Picking Accuracy",
      description:
        "Barcode scanning validation and AI pick path guidance eliminate mis-picks — achieving near-perfect order fulfilment accuracy.",
    },
    {
      value: "40%",
      metric: "Better Space Utilisation",
      description:
        "Intelligent slotting and bin management optimises every square metre of your warehouse — deferring expensive expansion.",
    },
  ],
  outcomeImage: "/assets/images/products/warehouse2.png",

  ctaTitle: "Transform Your Warehouse Into a Competitive Advantage",
  ctaSubtitle:
    "Book a free consultation and discover how EkkBaz AI can cut your warehouse labour costs by 50% and achieve 99.9% accuracy.",
  ctaLabel: "Book a Free Consultation",
  ctaLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
};

export const warehouseData = [
  {
    id: 1,
    extraLabel: "The Challenge",
    userType: "Warehouse Inefficiency Is Silently Draining Your Margins",
    description:
      "Manual warehouse management leads to stock discrepancies, picking errors, wasted space, and excessive labor costs. Without real-time inventory visibility, your warehouse team spends more time searching for products than moving them — creating bottlenecks that slow fulfillment and frustrate customers.",
    banner: "/assets/images/products/warehouse1.png",
  },
  {
    id: 2,
    extraLabel: "Our Solution",
    userType: "Intelligent Warehouse Management Powered by AI",
    description:
      "EkkBaz AI transforms your warehouse into a precision operation. Maintain perfectly accurate stock levels with real-time inventory tracking. Automatically publish and unpublish products based on availability to prevent order failures. AI-driven putaway and picking optimization reduces warehouse labor time by 50% while dramatically improving accuracy. Every product movement is tracked, timestamped, and recorded automatically.",
    banner: "/assets/images/products/warehouse2.png",
  },
];

export const warehouseFAQs = [
  {
    question: "How does EkkBaz AI improve warehouse picking accuracy?",
    answer:
      "EkkBaz AI generates optimized pick lists that minimize travel distance within the warehouse, and uses barcode scanning with real-time validation to eliminate mis-picks — achieving 99.9% picking accuracy and reducing labor time per order by 50%.",
  },
  {
    question: "How does EkkBaz AI handle automatic product availability management?",
    answer:
      "EkkBaz AI automatically publishes products to your sales channels when stock is available and unpublishes them when inventory reaches zero — preventing overselling and maintaining trust.",
  },
  {
    question: "Can EkkBaz AI manage multiple warehouses from one platform?",
    answer:
      "Yes. EkkBaz AI provides a unified multi-warehouse management system with location-level visibility, inter-warehouse transfer management, and AI-driven stock allocation recommendations.",
  },
];
